import { PaymentLinkGetResponseType } from "@sellar-io/api-v2/types";
import {
  calculateItemTotal,
  calculateVatTotal,
  formatCurrency,
} from "@sellar-io/common";
import dayjs from "dayjs";
import { OrderSummaryContainer, ProfilePicture } from "../../../components";
import {
  TabbedDrawer,
  TabbedDrawerHeader,
  TabbedDrawerTitle,
  TabbedDrawerNavigation,
  TabbedDrawerTab,
} from "@sellar-io/components";
import { OrderCommentListContent } from "../../../components/newOrderSummary/OrderCommentsList/OrderCommentListContent";
import { usePaymentLinkOrderCommentCreate } from "../../../hooks/api/v2/paymentLink/usePaymentLinkOrderCommentCreate";
import { usePaymentLinkOrderCommentList } from "../../../hooks/api/v2/paymentLink/usePaymentLinkOrderCommentList";
import { renderTaxName } from "../../../utils/sellarHelpers";
import { OrderSendMessageForm } from "../../order/Message/OrderSendMessageForm";
import { SharedOrderCards } from "../../order/SharedOrderCards";
import { InvoiceDrawerTabs, tabs } from "./PaymentLinkDrawer.shared";

export const PaymentLinkDrawer = ({
  active,
  setActive,
  order,
  initialTab,
  showComments = true,
}: {
  active: boolean;
  setActive: (value: boolean) => void;
  order: PaymentLinkGetResponseType;
  initialTab: InvoiceDrawerTabs;
  showComments?: boolean;
}) => {
  return (
    <TabbedDrawer active={active} setActive={setActive} initTab={initialTab}>
      <TabbedDrawerHeader>
        <TabbedDrawerTitle
          title={order.Supplier.Business.name}
          subtitle={`Placed on ${dayjs(order.createdAt).format("DD/MM/YYYY")}`}
          customIcon={
            <ProfilePicture
              logoUrl={order.Supplier.Business.logoUrl}
              fallback="supplier-badge"
              size="large"
            />
          }
        />
        {showComments && <TabbedDrawerNavigation tabs={tabs} />}
      </TabbedDrawerHeader>

      <TabbedDrawerTab
        tab={InvoiceDrawerTabs.DETAILS}
        className={"bg-zinc-100"}
      >
        <OrderDetailsTab order={order} />
      </TabbedDrawerTab>

      {showComments && (
        <TabbedDrawerTab
          tab={InvoiceDrawerTabs.COMMENTS}
          className={"bg-zinc-100"}
        >
          <OrderCommentsTab orderId={order.id} />
        </TabbedDrawerTab>
      )}
    </TabbedDrawer>
  );
};

const OrderCommentsTab = ({ orderId }: { orderId: number }) => {
  const query = usePaymentLinkOrderCommentList({
    limit: 50,
    sortBy: "createdAt",
    sortDir: "asc",
    query: [
      {
        type: "OrderId",
        modifier: "EQUALS",
        value: orderId,
      },
    ],
  });
  const mutation = usePaymentLinkOrderCommentCreate();
  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto p-4">
        <OrderCommentListContent query={query} orderId={orderId} />
      </div>
      <div className="m-4 rounded-lg">
        <OrderSendMessageForm mutation={mutation} orderId={orderId} />
      </div>
    </div>
  );
};
const OrderDetailsTab = ({ order }: { order: PaymentLinkGetResponseType }) => {
  return (
    <div className={"m-4 flex flex-col gap-4"}>
      <OrderSummaryContainer
        items={order.OrderItems.map((orderItem) => ({
          description: orderItem.Product.name ?? "",
          quantity: orderItem.quantity,
          cost: formatCurrency(orderItem.price),
          sku: orderItem.Product.sku,
          fullPrice: orderItem.fullPrice,
          price: orderItem.price,
          ProductId: orderItem.ProductId,
          ParentId: orderItem.Product.ParentId,
          promotionAppliedAt: orderItem.promotionAppliedAt,
        }))}
        priceProps={{
          delivery: formatCurrency(order.deliveryCharge, order.currency),
          isDeliveryChargeCapped: order.deliveryChargeCapped,
          subTotal: order.OrderItems
            ? formatCurrency(
                calculateItemTotal(order.OrderItems),
                order.currency,
              )
            : "",
          discount:
            order.discountAmount > 0
              ? {
                  value: formatCurrency(order.discountAmount, order.currency),
                  calculatedTotal: formatCurrency(
                    order.discountAmount,
                    order.currency,
                  ),
                }
              : undefined,
          tax: {
            name: renderTaxName(order.currency),
            percentage: order.vatPercentage.toString(),
            total: formatCurrency(
              calculateVatTotal({ ...order, orderItems: order.OrderItems }),
              order.currency,
            ),
          },
          total: formatCurrency(order.price, order.currency),
        }}
      />
      <SharedOrderCards order={order} showComments={false} readOnly />
    </div>
  );
};
